import {
  Table,
  Tag,
  Button,
  Tooltip,
  Spin,
  Modal,
  Select,
  Row,
  Col,
  Popconfirm
} from "antd";

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import useCertificationRequests from "./logic/useCertificationRequest";
import CertificationRequestService from "./logic/CertificationRequestService";
import "./CertificationRequestValidation.scss";

const { Option } = Select;
function CertificationRequestValidation() {
  const { certificationRequests, isLoading, approveRequest, rejectRequest } =
    useCertificationRequests();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentCertUrl, setCurrentCertUrl] = useState(null);
  const [currentCertType, setCurrentCertType] = useState(null);
  const [loadingCert, setLoadingCert] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const filteredRequests =
    statusFilter === "All"
      ? certificationRequests
      : certificationRequests.filter((req) => req.status === statusFilter);

  if (isLoading) return <Spin size="large" />;

  const fetchCertificate = async (certId, certPath) => {
    try {
      setLoadingCert(true);
      const fileUrl = await CertificationRequestService.getCertificateUrl(
        certId
      );
      if (fileUrl) {
        const fileExtension = certPath.split(".").pop().toLowerCase();
        const isPdf = fileExtension === "pdf";
        setCurrentCertUrl(fileUrl);
        setCurrentCertType(isPdf ? "pdf" : "image");
        setIsModalVisible(true);
      }
    } catch (e) {
      console.error("❌ Error fetching certificate:", e);
    } finally {
      setLoadingCert(false);
    }
  };

  const columns = [
    {
      title: "Certificate Title",
      dataIndex: "title",
      key: "title",
      render: (title) => title || <Tag color="red">No Title</Tag>,
    },
    {
      title: "Skill(s)",
      dataIndex: "certifiedSkills",
      key: "skills",
      render: (certifiedSkills) => {
        return (
          <>
            {certifiedSkills?.length > 0 ? (
              certifiedSkills.map((skill) => (
                <Tag color="blue" key={skill?.skill?.id}>
                  {skill?.skill?.name || "Unknown Skill"} - Level{" "}
                  {skill?.level || "N/A"}
                </Tag>
              ))
            ) : (
              <Tag color="red">No Skills Found</Tag>
            )}
          </>
        );
      },
    },
    {
      title: "Requester",
      dataIndex: "user",
      key: "user",
      render: (user) => {
        if (!user) {
          return <Tag color="red">Unknown User</Tag>;
        }
        return (
          <span>
            {user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.name || "Unknown User"}
          </span>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "createdAt",
      render: (created_at) =>
        created_at ? new Date(created_at).toLocaleDateString() : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <>
          {status === "Approved" && <Tag color="green">Approved</Tag>}
          {status === "Pending" && <Tag color="blue">Pending</Tag>}
          {status === "Rejected" && <Tag color="red">Rejected</Tag>}
        </>
      ),
    },
    {
      title: "Certificate",
      key: "certificate",
      render: (record) => (
        <div className="cert-actions">
          {record.cert_path ? (
            <>
              <Tooltip title="View Certificate">
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => fetchCertificate(record.id, record.cert_path)}
                />
              </Tooltip>
              <Tooltip title="Download Certificate">
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    CertificationRequestService.downloadCertificate(record.id)
                  }
                />
              </Tooltip>
            </>
          ) : (
            <Tag color="red">No File</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <div className="actions">
          <Popconfirm
            title="Are you sure you want to approve this certification?"
            onConfirm={() =>
              approveRequest.mutate({
                certId: record.id,
                approverId: "approver-id",
              })
            }
            okText="Yes"
            cancelText="No"
            disabled={record.status === "Approved"}
          >
            <Button
              type="primary"
              disabled={record.status === "Approved" || approveRequest.isLoading}
            >
              Validate
            </Button>
          </Popconfirm>
    
          <Popconfirm
            title="Are you sure you want to reject this certification?"
            onConfirm={() =>
              rejectRequest.mutate({
                certId: record.id,
                approverId: "approver-id",
              })
            }
            okText="Yes"
            cancelText="No"
            disabled={record.status === "Rejected" || record.status === "Approved"}
          >
            <Button
              danger
              disabled={
                record.status === "Rejected" ||
                record.status === "Approved" ||
                rejectRequest.isLoading
              }
              style={{ marginLeft: "10px" }}
            >
              Reject
            </Button>
          </Popconfirm>
        </div>
      ),
    }
    
  ];

  return (
    <div className="cert-validation-container">
      <Row style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: "100%" }}
          >
            <Option value="All">All</Option>
            <Option value="Pending">Pending</Option>
            <Option value="Approved">Approved</Option>
            <Option value="Rejected">Rejected</Option>
          </Select>
        </Col>
      </Row>

      <Table columns={columns} dataSource={filteredRequests} rowKey="id" />

      <Modal
        visible={isModalVisible}
        footer={null}
        onCancel={() => setIsModalVisible(false)}
        title="Certification Preview"
        width={800}
      >
        {loadingCert ? (
          <Spin />
        ) : currentCertType === "pdf" ? (
          <embed
            src={currentCertUrl}
            type="application/pdf"
            width="100%"
            height="600px"
          />
        ) : (
          <img
            src={currentCertUrl}
            alt="Certification"
            style={{ width: "100%" }}
          />
        )}
      </Modal>
    </div>
  );
}

export default CertificationRequestValidation;
